/**
 * 盤面の描画。
 *
 * `core` の盤面状態を受け取って DOM に反映するだけで、**判断を持たない**
 * （`04-architecture.md` board-view）。どのピースがどのグループかも、いま第何段階かも、
 * 呼び出し側が決めて渡す。
 *
 * 根拠: spec/02-requirements.md FR-006 FR-012 FR-021、spec/04-architecture.md、ADR-007。
 */

import type { Groups } from '../core/groups';
import type { PieceId } from '../core/types';
import { DISPLACE_MS, JOIN_FLASH_MS, SECRET_SHAKE_MS } from './constants';
import { at } from './internal';
import {
  createPiece,
  EDGES,
  type PieceElements,
  pieceSize,
  setPieceCell,
  setSeamState,
} from './piece-view';
import { QR_SVG } from './qr';
import { type Phase, seamState } from './seams';
import './board.css';
import type { Placement, TargetLayout } from '../core/types';
import type { QrCorner } from '../shell/stage-config-schema';

/** 盤面の見た目を決める、ステージごとに固定の値。 */
export interface BoardViewOptions {
  readonly C: number;
  readonly R: number;
  /** 原画像の `background-image` 値。`cssImageValue()` で作る。 */
  readonly image: string;
  /**
   * ピース別タイルの `background-image` 値（TK-035）。ピース `p` のタイルを返す。
   * 無いステージ（開発用の仮ステージ等）では原画像の切り出しで描く。
   */
  readonly tileImage?: (piece: number) => string;
  /** 秘文字画像の `background-image` 値。全ステージ共通の素材。 */
  readonly secret: string;
  /** 秘文字の目標配置 `τ`。ピース `p` には断片 `τ[p]` を重ねる（`04a` §1.5）。 */
  readonly secretLayout: TargetLayout;
  /**
   * 秘文字レイヤー退場の移動ベクトル（論理 px。TK-037）。開始演出の進入方角と同じ側へ
   * フレームアウトさせる。無ければその場でのフェードアウトに後退する。
   */
  readonly secretExit?: { readonly x: number; readonly y: number };
  /**
   * 盤面の代替テキスト（NFR-006）。
   *
   * 原画像は `background-image` として敷かれるため、支援技術には一切露出しない。
   * 画像そのものに `alt` を与える場所が無いので、盤面全体を 1 枚の画像として名づける。
   */
  readonly label: string;
  /**
   * QR 刻印の隅（TK-058）。完成表示にサイト URL の QR を不透明度 70% で重ねる
   * （スクリーンショット経由の流入導線）。Pipeline D が画像から決める。無ければ右下。
   */
  readonly qrCorner?: QrCorner;
}

/** 1 回の描画で渡す盤面の状態。 */
export interface BoardRenderState {
  /** 各セルにいるピース。 */
  readonly placement: Placement;
  /** 現在の目標配置から導出したグループ。 */
  readonly groups: Groups;
  /**
   * 段階。継ぎ目の表示規則が段階によって異なる（FR-021、`view/seams.ts`）。
   * 第 1 段階は結合の有無にかかわらず継ぎ目を描き、第 2 段階は結合した辺の継ぎ目を消す。
   */
  readonly phase: Phase;
}

/** 1 回の描画の付帯情報。 */
export interface BoardRenderOptions {
  /**
   * このドロップで押しのけられたピース（FR-020）。旧位置から新位置まで 0.2 秒の
   * CSS transition で補間される。モデル（配置）は即座に更新済みであることが前提。
   */
  readonly displaced?: readonly PieceId[];
  /**
   * このドロップでドラッグされていたグループ（TK-052）。ドロップで直接できた結合は
   * 着地を待たず最初に光る。
   */
  readonly dragged?: readonly PieceId[];
}

/** 盤面ビューのハンドル。 */
export interface BoardView {
  /** 盤面状態を描画に反映する。 */
  readonly render: (state: BoardRenderState, options?: BoardRenderOptions) => void;
  /** 秘文字レイヤーの表示。第 1 段階の完了でフェードアウトさせる（FR-013、TK-013）。 */
  readonly setSecretVisible: (visible: boolean) => void;
  /** 生成したピース要素（ドラッグのヒットテスト用。TK-011 が使う）。 */
  readonly pieceElement: (piece: number) => HTMLElement;
  /**
   * 完成表示レイヤーへ原画像 URL を書き込む（TK-036）。クリア確定（CLEARING）で呼ぶ。
   * 表示そのものは `board--cleared` の CSS が行う。
   */
  readonly revealComplete: () => void;
}

/**
 * `host` の中に盤面を組み立てる。
 *
 * 要素はここで 1 度だけ作り、以後の `render()` では位置と継ぎ目の表示だけを書き換える。
 * ピースの生成・破棄を繰り返すと、ドラッグ中の要素が差し替わって追従が途切れる。
 */
export function createBoardView(host: HTMLElement, options: BoardViewOptions): BoardView {
  const { C, R, secretLayout } = options;
  const N = C * R;
  const size = pieceSize(C, R);

  host.replaceChildren();
  host.setAttribute('role', 'img');
  host.setAttribute('aria-label', options.label);
  host.style.setProperty('--piece-w', `${size.w}px`);
  host.style.setProperty('--piece-h', `${size.h}px`);
  // タイルのあるステージでは原画像 URL を DOM に書かない（TK-035）。原画像は closure に
  // 保持し、クリア確定の revealComplete で初めて完成表示レイヤーへ書き込む（TK-036）。
  if (options.tileImage === undefined) host.style.setProperty('--stage-image', options.image);
  host.style.setProperty('--secret-image', options.secret);
  if (options.secretExit !== undefined) {
    host.style.setProperty('--secret-exit-x', `${options.secretExit.x}px`);
    host.style.setProperty('--secret-exit-y', `${options.secretExit.y}px`);
  }

  const pieces: PieceElements[] = [];
  const fragment = document.createDocumentFragment();
  for (let piece = 0; piece < N; piece++) {
    const elements = createPiece(
      piece,
      at(secretLayout, piece),
      C,
      size,
      options.tileImage?.(piece),
    );
    pieces.push(elements);
    fragment.append(elements.root);
  }
  host.append(fragment);

  // 盤面外周の枠。段階にも結合状態にも依存せず常に描画する（FR-021）。ピースより手前に
  // 置くのは、外周のピースが持つ自分の辺のベベルと重ならずに縁を締めるため。
  // 4 辺はアトラスから切り出すベベル帯で、外縁のピース辺と V 字の谷を作る（TK-030）。
  const frame = document.createElement('div');
  frame.className = 'board-frame';
  for (const edge of EDGES) {
    const strip = document.createElement('div');
    strip.className = `frame-bevel frame-bevel--${edge}`;
    frame.append(strip);
  }
  host.append(frame);

  // 完成表示と終了演出（TK-036）。クリアで `board-complete` が原画像 1 枚を表示して
  // タイルの継ぎ目（サブピクセルの隙間）を覆い、`board-outro` が同じ画像を α0.5 から
  // 緩やかに拡大しながらフェードアウトさせる。どちらも `board--cleared` の付与（CSS）で
  // 駆動され、プレイ中は原画像を DOM に持たない（TK-035 の趣旨を保つため URL は
  // クリア時に初めて書き込む）。
  // 秘文字退場の振動タイマー（TK-049）。再表示（setSecretVisible(true)）で破棄する。
  let secretShakeTimer: ReturnType<typeof setTimeout> | null = null;

  const complete = document.createElement('div');
  complete.className = 'board-complete';
  // QR 刻印（TK-058）。完成表示と一緒に現れる（親の opacity 遷移に乗る）。
  // 1 モジュール = 1 CSS px で表示するため、寸法は CSS が倍率で割り戻す。
  const stamp = document.createElement('div');
  stamp.className = `qr-stamp qr-stamp--${options.qrCorner ?? 'br'}`;
  stamp.setAttribute('aria-hidden', 'true');
  stamp.innerHTML = QR_SVG;
  complete.append(stamp);
  const outro = document.createElement('div');
  outro.className = 'board-outro';
  host.append(complete, outro);
  const revealComplete = (): void => {
    complete.style.setProperty('--complete-image', options.image);
    outro.style.setProperty('--complete-image', options.image);
  };

  // 押しのけアニメーション中のピース（FR-020）。クラスの付与が transition を有効にし、
  // 期限が来たら外す。transitionend に頼らないのは、reduced-motion 等で transition が
  // 走らないとイベント自体が発火しないため。
  const displacing = new Map<PieceId, ReturnType<typeof setTimeout>>();
  const beginDisplace = (piece: PieceId): void => {
    const root = at(pieces, piece).root;
    root.classList.add('piece--displacing');
    const pending = displacing.get(piece);
    if (pending !== undefined) clearTimeout(pending);
    displacing.set(
      piece,
      setTimeout(() => {
        root.classList.remove('piece--displacing');
        displacing.delete(piece);
      }, DISPLACE_MS),
    );
  };

  // 新規結合のフラッシュ（TK-051 / TK-052）。前回描画時のグループ（構成キーとサイズ）を
  // 覚えておき、グループが「育った」ピースを含むセット全体を光らせる。初回描画は基準線。
  //
  // 同時に光ると混乱するため（TK-052）、フラッシュはキューで直列化する:
  // - 開始は**押しのけ・着地の移動が完了した後**（ドロップから DISPLACE_MS 後）
  // - 複数の独立したグループが同時に成立したら、**上から・左から**（スキャン順で最初に
  //   ピースが現れるグループから）順に、前のグループの消灯後に次を点灯する
  const prevGroupKey = new Array<string>(N).fill('');
  const prevGroupSize = new Array<number>(N).fill(1);
  let groupsBaselined = false;
  const flashTimers = new Map<number, ReturnType<typeof setTimeout>>();
  const beginFlash = (piece: number): void => {
    const root = at(pieces, piece).root;
    const pending = flashTimers.get(piece);
    if (pending !== undefined) {
      // 連続結合ではフラッシュを最初からやり直す（クラスを外して再適用）。
      clearTimeout(pending);
      root.classList.remove('piece--flash');
      void root.offsetWidth;
    }
    root.classList.add('piece--flash');
    flashTimers.set(
      piece,
      setTimeout(() => {
        root.classList.remove('piece--flash');
        flashTimers.delete(piece);
      }, JOIN_FLASH_MS),
    );
  };

  /** 点灯待ちのグループ。earliest は着地完了（DISPLACE_MS 経過）の時刻。 */
  const flashQueue: { pieces: readonly number[]; earliest: number }[] = [];
  let flashRunning = false;
  const runFlashQueue = (): void => {
    if (flashRunning) return;
    const next = flashQueue.shift();
    if (next === undefined) return;
    flashRunning = true;
    setTimeout(
      () => {
        for (const piece of next.pieces) beginFlash(piece);
        // 消灯を待ってから次のグループへ。
        setTimeout(() => {
          flashRunning = false;
          runFlashQueue();
        }, JOIN_FLASH_MS);
      },
      Math.max(0, next.earliest - Date.now()),
    );
  };

  const render = (state: BoardRenderState, options?: BoardRenderOptions): void => {
    const { placement, groups, phase } = state;
    // 第 2 段階に入ったら秘文字を完全に消したままにする（TK-037 の退場の後始末）。
    host.classList.toggle('board--secret-gone', phase === 2);

    // 新規結合の検出（TK-051）。グループの構成が変わり、かつサイズが増えたピースを含む
    // グループ = このドロップで結合が成立したセット（押しのけの副産物で偶然できた
    // グループも同じ規則で拾われる）。着地完了後にスキャン順で順番に光らせる（TK-052）。
    const memberKeys = groups.members.map((m) => m.join(','));
    const flashLabels = new Set<number>();
    if (groupsBaselined) {
      for (let piece = 0; piece < N; piece++) {
        const label = at(groups.labelOf, piece);
        const key = memberKeys[label] ?? '';
        const size = groups.members[label]?.length ?? 1;
        if (size > 1 && key !== at(prevGroupKey, piece) && size > at(prevGroupSize, piece)) {
          flashLabels.add(label);
        }
      }
      if (flashLabels.size > 0) {
        // 各ピースの現在セル（スキャン順の判定に使う）。
        const positionOf = new Array<number>(N).fill(0);
        for (const [cell, piece] of placement.entries()) positionOf[piece] = cell;
        const draggedSet = new Set(options?.dragged ?? []);
        const entries = [...flashLabels].map((label) => {
          const members = groups.members[label] ?? [];
          return {
            members,
            minCell: Math.min(...members.map((piece) => at(positionOf, piece))),
            // ドロップで直接できた結合（ドラッグしたグループを含む）は着地を待たず最初に光る。
            direct: members.some((piece) => draggedSet.has(piece)),
          };
        });
        const now = Date.now();
        // 直接結合 → 押しのけ由来（上から・左から = セル番号昇順）の順。
        for (const entry of entries.filter((e) => e.direct)) {
          flashQueue.push({ pieces: entry.members, earliest: now });
        }
        for (const entry of entries
          .filter((e) => !e.direct)
          .sort((a, b) => a.minCell - b.minCell)) {
          flashQueue.push({ pieces: entry.members, earliest: now + DISPLACE_MS });
        }
        runFlashQueue();
      }
    }
    for (let piece = 0; piece < N; piece++) {
      const label = at(groups.labelOf, piece);
      prevGroupKey[piece] = memberKeys[label] ?? '';
      prevGroupSize[piece] = groups.members[label]?.length ?? 1;
    }
    groupsBaselined = true;
    // クラスは位置の書き込みと同じフレームで付ける。アニメーション完了前に再度
    // 押しのけられた場合はタイマーが延長され、現在位置から新しい移動先へ滑らかに継続する。
    for (const piece of options?.displaced ?? []) beginDisplace(piece);

    for (const [cell, piece] of placement.entries()) {
      const elements = at(pieces, piece);
      setPieceCell(elements, cell, C, size);

      for (const edge of EDGES) {
        setSeamState(elements, edge, seamState(cell, edge, placement, groups, C, R, phase));
      }
    }
  };

  return {
    render,
    setSecretVisible: (visible) => {
      if (visible) {
        if (secretShakeTimer !== null) clearTimeout(secretShakeTimer);
        secretShakeTimer = null;
        host.classList.remove('board--secret-shake', 'board--secret-exit');
        host.style.setProperty('--secret-opacity', '1');
        return;
      }
      // 退場は 2 拍（TK-049）: まずレイヤー全体を SECRET_SHAKE_MS だけ振動させ、
      // それから進入方角ベクトルがあればフレームアウト（TK-037）、無ければ従来のフェード。
      host.classList.add('board--secret-shake');
      secretShakeTimer = setTimeout(() => {
        secretShakeTimer = null;
        host.classList.remove('board--secret-shake');
        if (options.secretExit !== undefined) host.classList.add('board--secret-exit');
        else host.style.setProperty('--secret-opacity', '0');
      }, SECRET_SHAKE_MS);
    },
    pieceElement: (piece) => at(pieces, piece).root,
    revealComplete,
  };
}

/**
 * ステージ設定の画像指定を `background-image` の値へ変換する。
 *
 * AVIF と WebP の両方があるときは `image-set()` で形式交渉をブラウザに委ねる
 * （`05-database-schema.md` §3）。`background-image` にすることで、断片の切り出しが
 * `background-position` だけで済む（ADR-007）。
 */
export function cssImageValue(
  image: string | { readonly avif?: string; readonly webp?: string },
): string {
  if (typeof image === 'string') return `url("${safeUrl(image)}")`;

  const sources: string[] = [];
  if (image.avif !== undefined) sources.push(`url("${safeUrl(image.avif)}") type("image/avif")`);
  if (image.webp !== undefined) sources.push(`url("${safeUrl(image.webp)}") type("image/webp")`);
  if (sources.length === 0) throw new Error('ステージ設定の image に有効な URL がありません。');
  if (sources.length === 1) {
    // 1 形式しかないなら image-set にする意味がない。
    const only = image.avif ?? image.webp;
    return `url("${safeUrl(only as string)}")`;
  }
  return `image-set(${sources.join(', ')})`;
}

/**
 * CSS の `url()` に埋め込める形かを検査する。
 *
 * 値の出所はビルドが生成したページであり外部入力ではないが、引用符や括弧が混ざると
 * CSS 宣言そのものが壊れて盤面が真っ黒になる。壊れ方が分かりにくいので、ここで落とす。
 */
function safeUrl(url: string): string {
  if (!/^[\w./-]+$/.test(url)) {
    throw new Error(`画像 URL に使えない文字が含まれています: ${url}`);
  }
  return url;
}
